import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function RedeemPromo() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [step, setStep] = useState('code'); // code → details → success
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialDays, setTrialDays] = useState(null);
  const [result, setResult] = useState(null);

  // If code came from URL, validate immediately
  useEffect(() => {
    if (searchParams.get('code')) {
      validateCode(searchParams.get('code'));
    }
  }, []);

  async function validateCode(c) {
    setError('');
    try {
      const res = await fetch('/api/promo/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: c }),
      });
      const data = await res.json();
      if (data.valid) {
        setTrialDays(data.trial_days);
        setStep('details');
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
  }

  async function handleRedeem(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/promo/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, email, password, name }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStep('success');
      } else {
        setError(data.error || 'Redemption failed');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%', padding: '14px 16px', border: '1px solid #e2e8f0', borderRadius: 12,
    fontSize: 16, fontFamily: 'inherit', background: '#f8fafc', color: '#0f172a',
    outline: 'none', boxSizing: 'border-box',
  };
  const btnStyle = {
    width: '100%', padding: '14px', background: '#0f172a', color: '#fff', border: 'none',
    borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <img src="/favicon/favicon-96x96.png" alt="Photo Harbor" style={{ width: 64, height: 64, borderRadius: 14, marginBottom: 16, objectFit: 'contain', display: 'block', margin: '0 auto 16px' }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 4, letterSpacing: '-0.02em' }}>Photo Harbor</h1>

        {step === 'code' && (
          <>
            <p style={{ color: '#4a5f5d', fontSize: 15, marginBottom: 32 }}>Enter your access code to get started.</p>
            {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 16, padding: '10px 14px', background: '#fef2f2', borderRadius: 10 }}>{error}</div>}
            <input
              value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="DAILEY-XXXX" autoFocus
              style={{ ...inputStyle, textAlign: 'center', fontSize: 22, fontWeight: 700, letterSpacing: '0.05em', fontFamily: 'monospace', marginBottom: 16 }}
            />
            <button onClick={() => validateCode(code)} disabled={code.length < 6} style={{ ...btnStyle, opacity: code.length < 6 ? 0.5 : 1 }}>
              Verify Code
            </button>
          </>
        )}

        {step === 'details' && (
          <>
            <div style={{ display: 'inline-block', padding: '6px 16px', background: '#f0f5f4', color: '#1b2c2d', borderRadius: 20, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              {trialDays} days free
            </div>
            <p style={{ color: '#4a5f5d', fontSize: 14, marginBottom: 24 }}>Create your account to activate your trial.</p>
            {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 16, padding: '10px 14px', background: '#fef2f2', borderRadius: 10 }}>{error}</div>}
            <form onSubmit={handleRedeem}>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={{ ...inputStyle, marginBottom: 10 }} />
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" required style={{ ...inputStyle, marginBottom: 10 }} />
              <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password" type="password" required minLength={8} style={{ ...inputStyle, marginBottom: 20 }} />
              <button type="submit" disabled={loading} style={{ ...btnStyle, opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Setting up your account...' : 'Start Free Trial'}
              </button>
            </form>
            <div style={{ marginTop: 16, fontSize: 12, color: '#94a3b8' }}>
              Code: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
            </div>
          </>
        )}

        {step === 'success' && (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>You're in!</h2>
            <p style={{ color: '#4a5f5d', fontSize: 14, marginBottom: 8 }}>
              Your {trialDays}-day trial is active. Upload your first photos.
            </p>
            <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 24 }}>
              Trial ends {new Date(result.trial_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <button onClick={() => { if (result.access_token) { localStorage.setItem('auth_token', result.access_token); } window.location.href = '/'; }} style={btnStyle}>
              Open Photo Harbor
            </button>
          </>
        )}
      </div>
    </div>
  );
}
